import { useEffect, useState } from 'react';
import {
  LOOK_PART_LABELS,
  LOOK_PRICES_PENCE,
  formatPence,
  lookChangeCost,
  type Appearance,
  type CharacterOptionsReply,
  type LifeView,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { Button } from '@london-life/ui/components/button';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { toPreviewCharacter } from '@london-life/game/appearance';
import { api } from '../lib/api';
import { AvatarPreview } from '../world';
import { BodyFields, StyleFields } from '../life/look-fields';
import { actionKey } from '../lib/format';

const price = (pence: number) => formatPence(pence).replace(/\.00$/, '');

// Change the look: who you are (body, build, skin) is free to correct; hair, beard, outfit and accessories cost
// what they would in London. The total is shown before paying; the server charges it once.
export function LookEditor({
  life,
  onDone,
}: {
  life: LifeView;
  onDone: (life: LifeView | null, message?: string) => void;
}) {
  const [catalogue, setCatalogue] = useState<CharacterOptionsReply | null>(null);
  const [look, setLook] = useState<Appearance>(life.appearance);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  // one key for this attempt: a double tap or a retry pays once
  const [key, setKey] = useState(() => actionKey('look'));
  useEffect(() => {
    api
      .characterOptions()
      .then(setCatalogue)
      .catch((error: unknown) => setFailure(describeError(error)));
  }, []);
  const cost = lookChangeCost(life.appearance, look);
  const changed = JSON.stringify(look) !== JSON.stringify(life.appearance);
  const short = cost.totalPence > life.walletPence;
  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const reply = await api.life.look({ appearance: look }, key);
      setKey(actionKey('look'));
      onDone(reply.life, reply.message);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="look-editor">
      <div className="look-stage">
        <AvatarPreview character={toPreviewCharacter(look, life.firstName)} />
      </div>
      <div className="look-panel">
        {!catalogue && !failure && <p className="profile-hint">Loading the wardrobe…</p>}
        {catalogue && (
          <>
            <h3>Who you are</h3>
            <p className="profile-hint">Free to change at any time.</p>
            <BodyFields look={look} onChange={setLook} options={catalogue.options} keep={life.appearance} />
            <h3>Your style</h3>
            <StyleFields
              look={look}
              onChange={setLook}
              options={catalogue.options}
              keep={life.appearance}
              prices={{
                hair: price(LOOK_PRICES_PENCE.hair),
                hairColour: price(LOOK_PRICES_PENCE.hairColour),
                facialHair: price(LOOK_PRICES_PENCE.facialHair),
                outfit: price(LOOK_PRICES_PENCE.outfit),
                accessories: price(LOOK_PRICES_PENCE.accessories),
              }}
            />
          </>
        )}
        {failure && <ErrorNotice error={failure} />}
      </div>
      <footer className="look-footer">
        <p aria-live="polite">
          {cost.parts.length
            ? `${cost.parts.map((part) => LOOK_PART_LABELS[part]).join(', ')}: ${formatPence(cost.totalPence)}`
            : changed
              ? 'Free'
              : 'No changes yet'}
          {short && ' · not enough in your wallet'}
        </p>
        <Button type="button" variant="outline" onClick={() => onDone(null)} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" onClick={() => void save()} disabled={busy || !changed || short}>
          {busy ? 'Saving…' : cost.totalPence ? `Pay ${formatPence(cost.totalPence)}` : 'Save'}
        </Button>
      </footer>
    </div>
  );
}
